import { UseMultipleChartsType } from '@ecosystem/export-utils';
import { useCallback } from 'react';
import { CategoryLeaderboardPg } from '@models/CategoryLeaderboard';
import { DEFAULT_VOLUME } from '@utils/factVolumeUtils/FactVolumeUtils';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { FactRevenue, Option, PageName } from '@utils/pageConfig/Types';
import { ExportChartOptions } from '@hooks/export/Model';
import { useExportApi } from '@hooks/export/UseExportApi';
import { exportDoubleBarChart } from '../ExportDoubleBarChart';
import { useGetLoadedProductGroups } from '../../UseGetLoadedProductGroups';

export interface ExportDoubleBarChartOptions extends ExportChartOptions {
  title: string;
  appliedCountry?: Option[];
  appliedProductGroup?: Option[];
  data: CategoryLeaderboardPg[];
  period: string;
  comparisonTimePeriod: string;
  factRevenue: FactRevenue;
  periodType: string;
  appliedFiltersText: string;
  selectedKpi: Option;
  multipleChartsExport: UseMultipleChartsType;
  pageName: PageName;
  volume?: string;
  summaryData?: CategoryLeaderboardPg[];
  showPerformanceWithin?: string;
}

export const useExportDoubleBarChart = (): {
  download: (options: ExportDoubleBarChartOptions) => Promise<void>;
} => {
  const { exportFile: exportFn } = useExportApi();
  const pagesConfig = usePageConfig();

  const { loadedProductGroups } = useGetLoadedProductGroups();

  const download = useCallback(
    async ({
      exportFormats,
      invokeExport,
      title,
      appliedCountry,
      appliedProductGroup,
      data,
      period,
      comparisonTimePeriod,
      factRevenue,
      periodType,
      appliedFiltersText,
      selectedKpi,
      volume = DEFAULT_VOLUME,
      multipleChartsExport,
      pageName,
      summaryData,
      showPerformanceWithin = '',
    }: ExportDoubleBarChartOptions) => {
      await exportDoubleBarChart({
        exportFn: exportFn,
        exportFormats: exportFormats,
        invokeExport: invokeExport,
        title: title,
        appliedCountry: appliedCountry,
        appliedPg: appliedProductGroup,
        data: data,
        timePeriod: period,
        comparisonTimePeriod: comparisonTimePeriod,
        factRevenue: factRevenue,
        periodType: periodType,
        showPerformanceWithin: showPerformanceWithin,
        appliedFiltersText: appliedFiltersText,
        selectedKpi: selectedKpi,
        segmentBy: '',
        pageName: pageName,
        volume: volume,
        multipleChartsExport: multipleChartsExport,
        summaryData: summaryData,
        pagesConfig,
        ...loadedProductGroups,
      });
    },
    [pagesConfig, loadedProductGroups],
  );

  return {
    download,
  };
};
